import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../common/state/hooks";
import {
  getAllSessionsByProjectGroup,
  sessionsByProjectGroupSelector,
} from "../../state/statsSlice";
import { IsLoading } from "../../../../common/components/AppState/IsLoading";
import {
  ANONYMOUS_SESSION_COUNTER_COMPONENT,
  AnonymousSessionCounter,
} from "src/features/subscription/components/AnonymousSessionsCounter";
import {
  fetchAnonymousSessionsCount,
  getNumberOfPaywalledAnonymousSessions,
} from "src/features/subscription/state/subscriptionSlice";

// New stats components
import { ActivityHeatmap } from "src/features/statsNew/components/charts";
import SessionsPerMonthChart from "src/features/statsNew/components/charts/SessionsPerMonthChart";
import TopParcoursChart from "src/features/statsNew/components/charts/TopParcoursChart";
import { Link } from "react-router-dom";

// Import responsive styles
import "src/features/statsNew/components/statsNew.css";

export const STATS_OVERVIEW_PROJECT_GROUPS_COMPONENT_ID = "statsOverviewProjectGroupsComponent";
export const STATS_OVERVIEW_PROJECT_GROUPS_SESSIONS_COMPONENT_ID =
  "statsOverviewProjectGroupsSessionsComponent";
export const BUILDS_COMPONENT_ID = "buildsComponent";

const StatsOverview = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const projectGroupWithSessions = useAppSelector(sessionsByProjectGroupSelector);
  const noEligibleSession = projectGroupWithSessions.length === 0;
  const numberOfPaywalledAnonymousSessions = useAppSelector(getNumberOfPaywalledAnonymousSessions);

  // Check if the alert banner is visible
  const showAlertBanner = numberOfPaywalledAnonymousSessions > 0;

  // Empty date range to show all data
  const dateRange = { startDate: null, endDate: null };

  // Dynamic page size based on alert visibility
  const pageSize = showAlertBanner ? 7 : 9;

  // CSS class for alert-aware responsive height
  const chartContainerClass = `dashboard-chart-container ${showAlertBanner ? "with-alert" : ""}`;

  // Calculate total sessions
  const totalSessions = useMemo(() => {
    let count = 0;
    projectGroupWithSessions.forEach((group) => {
      group.projects.forEach((project) => {
        count += project.sessions.length;
      });
    });
    return count;
  }, [projectGroupWithSessions]);

  useEffect(() => {
    void dispatch(
      getAllSessionsByProjectGroup({
        componentId: STATS_OVERVIEW_PROJECT_GROUPS_SESSIONS_COMPONENT_ID,
      }),
    );
    void dispatch(
      fetchAnonymousSessionsCount({ componentId: ANONYMOUS_SESSION_COUNTER_COMPONENT }),
    );
  }, []);

  return (
    <IsLoading
      componentId={STATS_OVERVIEW_PROJECT_GROUPS_SESSIONS_COMPONENT_ID}
      showSuccess={false}
    >
      <div className={`stats-overview-container ${showAlertBanner ? "with-alert" : ""}`}>
        {/* Alert Banner - Compact styling */}
        {showAlertBanner && (
          <div className="w-full mb-2">
            <AnonymousSessionCounter
              numberOfPaywalledAnonymousSessions={numberOfPaywalledAnonymousSessions}
              className="bg-yellow-50 border border-yellow-200 rounded-lg w-full py-2 px-4"
              withSpinner={false}
            />
          </div>
        )}

        {noEligibleSession ? (
          <div className="flex items-center justify-center h-48 bg-white rounded-lg shadow-sm border border-gray-100">
            <p className="text-gray-500">{t("pages.statistics.noData")}</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2 flex-1">
            {/* Row 1: Stats Card + Activity Heatmap */}
            <div className="grid grid-cols-1 lg:grid-cols-[180px_1fr] gap-2 w-full">
              {/* Total Sessions - Hero Card */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col items-center justify-center p-4 min-h-[140px]">
                {/* Icon */}
                <div className="p-3 bg-green-100 rounded-full text-green-600 mb-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-7 w-7"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    />
                  </svg>
                </div>

                {/* Hero Number */}
                <p className="text-4xl font-bold text-gray-900 leading-none">{totalSessions}</p>

                {/* Label */}
                <p className="text-sm font-medium text-gray-500 mt-2">
                  {t("statsNew.global.totalSessions")}
                </p>

                {/* Divider + Link */}
                <div className="w-full border-t border-gray-100 mt-3 pt-3">
                  <Link
                    to="/statistics"
                    className="flex items-center justify-center gap-1 text-sm text-purple-500 hover:text-purple-600 transition-colors"
                  >
                    <span>{t("statsNew.global.viewDetails")}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </Link>
                </div>
              </div>

              {/* Activity Heatmap */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-3 min-w-0">
                <ActivityHeatmap />
              </div>
            </div>

            {/* Row 2: Charts side by side - Responsive height */}
            <div className="stats-charts-row">
              {/* Sessions per month - stacked bars */}
              <div
                className={`w-full bg-white rounded-lg shadow-sm border border-gray-100 p-3 flex flex-col ${chartContainerClass}`}
              >
                <p className="text-gray-800 font-medium text-sm mb-1 flex-shrink-0">
                  {t("statsNew.charts.sessionsPerMonth")}
                </p>
                <div className="flex-1 min-h-0">
                  <SessionsPerMonthChart dateRange={dateRange} />
                </div>
              </div>

              {/* Top parcours - horizontal bars */}
              <div
                className={`w-full bg-white rounded-lg shadow-sm border border-gray-100 p-3 flex flex-col ${chartContainerClass}`}
              >
                <p className="text-gray-800 font-medium text-sm mb-1 flex-shrink-0">
                  {t("statsNew.charts.topParcours")}
                </p>
                <div className="flex-1 min-h-0">
                  <TopParcoursChart dateRange={dateRange} pageSize={pageSize} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </IsLoading>
  );
};

export default StatsOverview;
